import type { FC } from "react";
import React, { useCallback } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { connect } from "react-redux";

import { rgba } from "@similarweb/styles";
import { Banner } from "@similarweb/ui-components";

import { TOGGLE_ON_COMPARE_DROPDOWN } from "app/action_types/website_analysis_action-types";
import { chosenSites } from "app/common/services/chosenSites";
import { CHART_COLORS } from "app/constants/ChartColors";
import { useLoading } from "app/custom-hooks/loadingHook";
import { i18nFilter } from "app/filters/i18nFilter";
import AudienceOverlapApiService from "app/pages/website-analysis/audience-overlap/AudienceOverlapApiService";
import { OverlapOverTime } from "app/pages/website-analysis/audience-overlap/OverlapOverTime/OverlapOverTime";
import { AssetsService } from "app/services/AssetsService";
import { useSimilarSites } from "app/services/sitesService/hooks/useSimilarSites";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

import { AudienceLoyalty } from "../audience-loyalty/AudienceLoyalty";

import { BannerContainer, OverLapContainer } from "./StyledComponents";
import { VennDiagramComponent } from "./VennDiagramComponent";

const colors = CHART_COLORS.compareMainColors;
const DEFAULT_OPACITY = 0.4;

interface IAudienceOverlapProps {
    toggleCompareButton?: (action: Record<"type", string>) => void;
}

const AudienceOverlapInner: FC<IAudienceOverlapProps> = (props) => {
    const { toggleCompareButton } = props;
    const chosenSitesLegends = chosenSites.sitelistForLegend();
    const { params } = useCurrentStateAndParams();
    const { audienceOverlapApiService } = React.useMemo(
        () => ({
            audienceOverlapApiService: new AudienceOverlapApiService(),
        }),
        [],
    );

    const isSingleMode = React.useMemo(() => {
        return chosenSitesLegends.length === 1;
    }, [chosenSitesLegends]);

    const { data: similarSitesData, isLoading: isSimilarSiteLoading } = useSimilarSites(
        params.key as string,
        { limit: 2 },
        {
            enabled: isSingleMode && !!params.key,
        },
    );

    const [OverlapData, OverlapDataOps] = useLoading();
    const [loyaltyData, loyaltyDataOps] = useLoading();
    React.useEffect(() => {
        if (!isSingleMode || (!isSimilarSiteLoading && similarSitesData)) {
            const apiParams: any = proNavigator.getApiParams(params);
            const keys = isSingleMode
                ? [params.key, ...(similarSitesData?.map((site) => site.Domain) || [])]
                : (params.key as string).split(",");
            const queryParams = {
                ...apiParams,
                keys: keys.join(","),
            };
            if (keys.length > 1) {
                OverlapDataOps.load(() => audienceOverlapApiService.getDomainsAudienceOverlap(queryParams));
            } else {
                OverlapDataOps.load(() => Promise.resolve(null));
            }
            loyaltyDataOps.load(() => audienceOverlapApiService.getDomainsAudienceLoyalty(queryParams));
        }
    }, [similarSitesData, isSingleMode, isSimilarSiteLoading, params]);
    const loadingStates = [useLoading.STATES.INIT, useLoading.STATES.LOADING];
    const isOverlapLoading = loadingStates.includes(OverlapData.state);
    const isLoyaltyLoading = loadingStates.includes(OverlapData.state);

    const websitesArrayWithColor = React.useMemo(() => {
        if (similarSitesData) {
            const sitesWithMain = [...similarSitesData];
            sitesWithMain.unshift({
                Domain: params.key,
                DomainWithoutSub: params.key,
                Rank: "",
                Favicon: "",
            });
            return sitesWithMain.map((site, idx) => {
                return {
                    isSuggested: idx > 0,
                    ...site,
                    color: colors[idx],
                    name: site.Domain,
                    icon: site.Favicon,
                };
            });
        }
        return [];
    }, [isSingleMode, params.key, similarSitesData]);

    const excelUrl = React.useMemo(() => {
        const apiParams: any = proNavigator.getApiParams(params);
        return audienceOverlapApiService.getDomainsAudienceOverlapExcelUrl({
            ...apiParams,
            ...(isSingleMode
                ? {
                      keys: [params.key, ...(similarSitesData?.map((site) => site.Domain) || [])].join(","),
                  }
                : { keys: params.key }),
        });
    }, [isSingleMode, similarSitesData, params.key]);

    const openQueryBarCompare = useCallback(() => {
        allTrackers.dispatchTrackEvent(
            "analysis.overlap.single.banner.click.to.compare",
            "click",
            "analysis.overlap.single.banner.click.to.compare",
        );
        if (websitesArrayWithColor?.length === 1) {
            toggleCompareButton({ type: TOGGLE_ON_COMPARE_DROPDOWN });
            return;
        }
        proNavigator.applyUpdateParams({
            key: websitesArrayWithColor.map(({ name }) => name).join(),
        });
    }, [websitesArrayWithColor]);

    return (
        <OverLapContainer>
            {isSingleMode && websitesArrayWithColor?.length > 0 ? (
                <BannerContainer>
                    <Banner
                        title={i18nFilter()("analysis.audience.overlap.singleMode.compareBanner.title")}
                        subtitle={i18nFilter()("analysis.audience.overlap.singleMode.compareBanner.subtitle")}
                        buttonType="primary"
                        iconImagePath={AssetsService.assetUrl("/images/Illustration-Benchmarking.svg")}
                        onButtonClick={openQueryBarCompare}
                        buttonIconName={"add"}
                        buttonText={i18nFilter()("compare")}
                        iconImageHeight={48}
                        iconImageWidth={48}
                    />
                </BannerContainer>
            ) : null}
            <VennDiagramComponent
                isVennDiagramLoading={isOverlapLoading}
                VennDiagramData={OverlapData}
                websitesArrayWithColor={websitesArrayWithColor}
                isSingleMode={isSingleMode}
                chosenSitesLegends={
                    isSingleMode
                        ? websitesArrayWithColor.map((websiteWithColor) => ({
                              ...websiteWithColor,
                              image: websiteWithColor.icon || websiteWithColor.Favicon || "",
                              smallIcon: true,
                          }))
                        : chosenSitesLegends
                }
                selectedCountryCode={params.country}
                excelDownloadUrl={excelUrl}
            />
            <OverlapOverTime
                rawData={OverlapData.data?.OverlapData}
                chosenSitesLegends={
                    isSingleMode
                        ? websitesArrayWithColor.map((websiteWithColor) => {
                              const { isSuggested, color } = websiteWithColor;
                              return {
                                  ...websiteWithColor,
                                  color: isSuggested ? rgba(color, DEFAULT_OPACITY) : color,
                              };
                          })
                        : chosenSitesLegends
                }
                isLoading={isOverlapLoading}
            />
            <AudienceLoyalty
                comparisonData={loyaltyData.data}
                isLoadingComparisonData={isLoyaltyLoading}
                isSingleMode={isSingleMode}
                chosenSitesLegends={chosenSitesLegends}
                websitesArrayWithColor={websitesArrayWithColor}
                selectedCountryCode={params.country}
            />
        </OverLapContainer>
    );
};

const mapDispatchToProps = (dispatch) => {
    return {
        toggleCompareButton: (action) => {
            dispatch(action);
        },
    };
};

export const AudienceOverlap = connect(null, mapDispatchToProps)(AudienceOverlapInner);
